import { useEffect } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { ArrowLeft, ExternalLink, Loader2, Users } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { usePermission } from "@/hooks/usePermission"
import { useChildOrgDetail } from "@/hooks/useSponsorships"
import { formatCurrencyCents } from "@/lib/sponsor"
import { FundLedgerSection } from "@/components/sponsor/FundLedgerSection"
import { TransferOutSection } from "@/components/sponsor/TransferOutSection"
import { DisbursementQueue } from "@/components/sponsor/DisbursementQueue"

export function SponsorOrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const { hasPermission, loading: permLoading } = usePermission("can_view_child_financials")
  const detail = useChildOrgDetail(orgId)

  const gateLoading = permLoading || detail.loading
  // The sponsorship row is only visible when the viewer's org sponsors this
  // child — its absence means "not yours", regardless of why.
  const allowed = hasPermission && detail.sponsorship !== null

  useEffect(() => {
    if (!gateLoading && !allowed) {
      navigate("/sponsor", { replace: true })
    }
  }, [gateLoading, allowed, navigate])

  if (gateLoading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
        <p className="text-muted-foreground">Loading project...</p>
      </div>
    )
  }

  if (!allowed || !detail.org) return null

  const totalPages = Math.max(1, Math.ceil(detail.donationsTotal / detail.pageSize))

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/sponsor">
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
          Back to sponsor console
        </Link>
      </Button>

      <div className="flex flex-wrap items-center gap-4">
        {detail.org.logo_url && (
          <img
            src={detail.org.logo_url}
            alt=""
            className="h-12 w-12 rounded-lg object-contain"
          />
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">{detail.org.name}</h1>
            {detail.sponsorship && (
              <Badge variant={detail.sponsorship.mode === "fiscal" ? "default" : "secondary"}>
                {detail.sponsorship.mode}
              </Badge>
            )}
            {detail.sponsorship?.status === "exited" && (
              <Badge variant="outline" className="border-gray-300 text-gray-600">
                Archived — sponsored period only
              </Badge>
            )}
          </div>
          <a
            href={`/${detail.org.slug}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
          >
            View public site
            <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        </div>
      </div>

      {detail.error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {detail.error}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Succeeded donations</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{detail.donationsTotal.toLocaleString("en-US")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Contacts</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" aria-hidden />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{detail.contactCount.toLocaleString("en-US")}</p>
          </CardContent>
        </Card>
      </div>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Recent donations</h2>
        {detail.donations.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-gray-500">
            No donations yet.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {["Date", "Donor", "Email", "Amount"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {detail.donations.map((d, i) => (
                  <tr key={`${d.created_at}-${i}`}>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(d.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">{d.donor_name || "Anonymous"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{d.donor_email ?? ""}</td>
                    <td className="px-4 py-3 tabular-nums">{formatCurrencyCents(d.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="mt-3 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {detail.page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={detail.page === 0}
                onClick={() => detail.setPage(detail.page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={detail.page >= totalPages - 1}
                onClick={() => detail.setPage(detail.page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </section>

      {detail.sponsorship?.mode === "fiscal" && detail.sponsorship.status !== "exited" && (
        <DisbursementQueue
          sponsorshipId={detail.sponsorship.id}
          canDecide={hasPermission}
          onDecided={detail.refresh}
        />
      )}

      {detail.sponsorship?.mode === "fiscal" && (
        <FundLedgerSection
          sponsorshipId={detail.sponsorship.id}
          childName={detail.org.name}
          canRecord={hasPermission && detail.sponsorship.status !== "exited"}
        />
      )}

      {detail.sponsorship && detail.sponsorship.status !== "exited" && (
        <TransferOutSection sponsorship={detail.sponsorship} onChanged={detail.refresh} />
      )}
    </div>
  )
}
